import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Card, Modal, Form, Input, Select, message, Typography, Tag, Popconfirm, Row, Col, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

const { Title } = Typography;
const { Option } = Select;

const LocationsPage = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const { user } = useAuth();
    const isAdmin = user?.role === 'ADMIN';
    const [locations, setLocations] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const fetchData = async () => {
        setLoading(true);
        try {
            const [locRes, branchRes] = await Promise.allSettled([
                client.get('/master/locations'),
                client.get('/master/branches'),
            ]);

            if (locRes.status === 'fulfilled') {
                setLocations(locRes.value.data);
            } else {
                message.error('Failed to fetch locations');
            }

            if (branchRes.status === 'fulfilled') {
                setBranches(branchRes.value.data);
            } else {
                message.error('Failed to fetch branches');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({
            ...record,
            branch_id: record.branch_id || record.branch?.id,
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await client.delete(`/master/locations/${id}`);
            message.success('Location deleted successfully');
            fetchData();
        } catch (error) {
            message.error('Failed to delete location');
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        try {
            if (editingId) {
                await client.put(`/master/locations/${editingId}`, values);
                message.success('Location updated successfully');
            } else {
                await client.post('/master/locations', values);
                message.success('Location created successfully');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) {
            const data = error.response?.data;
            const validationErrors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
            message.error(data?.message || validationErrors || 'Operation failed');
        }
    };

    const columns = [
        {
            title: 'Location Name',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Typography.Text strong style={{ color: 'var(--text-primary)' }}>{text}</Typography.Text>
        },
        {
            title: 'Location Code',
            dataIndex: 'location_code',
            key: 'location_code',
            render: (code) => code || '-',
        },
        {
            title: 'Branch / Hub',
            dataIndex: 'branch',
            key: 'branch',
            render: (branch) => branch?.name ? <Tag color="blue" style={{ borderRadius: 12 }}>{branch.name}</Tag> : '-',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type) => (
                <Tag style={{ borderRadius: 12, padding: '0 12px' }}>{type}</Tag>
            )
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'} style={{ borderRadius: 12 }}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined style={{ color: '#3b82f6' }} />}
                        onClick={() => handleEdit(record)}
                        className="action-btn"
                    />
                    <Popconfirm
                        title="Delete Location"
                        description="Are you sure you want to delete this location?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="text"
                            icon={<DeleteOutlined style={{ color: '#ef4444' }} />}
                            danger
                            className="action-btn"
                        />
                    </Popconfirm>
                </Space>
            ),
        }
    ].filter(col => col.key !== 'actions' || isAdmin);

    const StatCard = ({ title, value, icon, color, subText }) => (
        <Card className="premium-card" bodyStyle={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <Typography.Text style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</Typography.Text>
                    <Title level={3} style={{ margin: 0, fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--text-primary)' }}>
                        {value}
                    </Title>
                    {subText && <Typography.Text style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'block' }}>{subText}</Typography.Text>}
                </div>
                <div className="stat-icon" style={{ backgroundColor: `${color}15`, color: color }}>
                    {icon}
                </div>
            </div>
        </Card>
    );

    const stats = {
        total: locations.length,
        warehouses: (locations || []).filter(l => l.type === 'WAREHOUSE').length,
        mills: (locations || []).filter(l => l.type === 'MILL').length,
    };

    return (
        <div style={{ padding: isMobile ? '16px' : '24px 32px' }}>
            <div style={{ marginBottom: 32, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 0, justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0, fontWeight: 800, color: 'var(--text-primary)' }}>Operational Locations</Title>
                    <Typography.Text style={{ color: 'var(--text-secondary)', fontSize: 16 }}>Manage warehouses, mills, and farms within each branch.</Typography.Text>
                </div>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={handleAdd}
                        style={{ height: 45, borderRadius: 10, fontWeight: 600, padding: '0 24px' }}
                    >
                        Add Location
                    </Button>
                )}
            </div>

            <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Total Locations"
                        value={stats.total}
                        icon={<i className="fa-solid fa-map-location-dot"></i>}
                        color="#3b82f6"
                        subText="Active sites"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Warehouses"
                        value={stats.warehouses}
                        icon={<i className="fa-solid fa-warehouse"></i>}
                        color="#10b981"
                        subText="Storage facilities"
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <StatCard
                        title="Processing Mills"
                        value={stats.mills}
                        icon={<i className="fa-solid fa-industry"></i>}
                        color="#8b5cf6"
                        subText="Palay processing"
                    />
                </Col>
            </Row>

            <Card className="premium-card" bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={locations}
                    rowKey="id"
                    loading={loading}
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 900 } : undefined}
                    pagination={{ defaultPageSize: 12, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                />
            </Card>

            <Modal
                title={editingId ? 'Edit Location' : 'Create New Operational Site'}
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
                width={500}
                className="premium-modal"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ type: 'WAREHOUSE', is_active: true }}
                >
                    <Form.Item name="branch_id" label="Parent Branch" rules={[{ required: true, message: 'Branch selection is required' }]}>
                        <Select placeholder="Select branch" style={{ height: 40 }}>
                            {branches.map((branch) => (
                                <Option key={branch.id} value={branch.id}>
                                    {branch.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="name" label="Site / Location Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <Input style={{ height: 40, borderRadius: 8 }} />
                    </Form.Item>
                    <Form.Item name="location_code" label="Location Code">
                        <Input style={{ height: 40, borderRadius: 8 }} placeholder="Optional identifier" />
                    </Form.Item>
                    <Form.Item name="type" label="Site Type" rules={[{ required: true, message: 'Type selection is required' }]}>
                        <Select style={{ height: 40 }}>
                            <Option value="WAREHOUSE">Warehouse</Option>
                            <Option value="MILL">Mill</Option>
                            <Option value="FARM_LOT">Farm Lot</Option>
                            <Option value="PEN">Pen</Option>
                            <Option value="OFFICE">Office</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="is_active" label="Operational Status">
                        <Select style={{ height: 40 }}>
                            <Option value={true}>Active</Option>
                            <Option value={false}>Inactive</Option>
                        </Select>
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
                        <Button onClick={handleCancel} style={{ height: 40, borderRadius: 8, padding: '0 24px' }}>Cancel</Button>
                        <Button type="primary" htmlType="submit" style={{ height: 40, borderRadius: 8, padding: '0 24px', fontWeight: 600 }}>
                            {editingId ? 'Save Changes' : 'Create Location'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default LocationsPage;




